import { fetchService } from "services/fetchService";
import { post, get, del, put, patch } from "services/httpService";
import { BASE_URL } from "utils/constants";
import { createRepoGithub } from "./v2/CodeBuilder/service";

const SERVICE_URLS = {
  createStrategy: () => "my-strategies/",
  getStrategies: () => "my-strategies/",
  getMyStrategies: () => `my-strategies/`,
  getCommunityStrategies: () => `community-strategies/`,
  getTrendingStrategies: () => `strategy/trending-strategies/`,
  getRecommendedStrategies: () => `questionnaire/answers/`,
  getStrategyDetails: (strategyId) => `strategy/${strategyId}/details/`,
  getPublicStrategyDetails: (strategyId) =>
    `strategy/${strategyId}/details-public/`,
  // Explore V2 - Strategy Page endpoints
  getStrategyOverview: (strategyId) =>
    `strategy/${strategyId}/strategy-overview/`,
  getActiveHoldings: (strategyId) => `strategy/${strategyId}/active-holdings/`,
  getAuthorOrderHistory: (strategyId) =>
    `strategy/${strategyId}/author-order-history/`,
  getSimilarStrategies: (strategyId) =>
    `strategy/${strategyId}/similar-strategies/`,
  getAllStrategyHoldings: (strategyId) =>
    `strategy/${strategyId}/all-strategy-holdings/`,
  predefinedScenariosAPI: () => "backtest-scenarios/all/",
  backtestStrategy: (strategyId) => `strategy/${strategyId}/backtest/`,
  strategyAPI: (strategyId) => `strategy/${strategyId}/edit/`,
  strategyBulkDelete: () => `strategy/edit/`,
  cloneStrategy: () => `strategy/clone/`,
  saveStrategyDescription: () => `strategy/edit-description/`,
  hostStrategy: (strategyId) => `strategy/${strategyId}/publish/`,
  getStrategyTemplates: () => `strategy-templates/`,
  generateStrategy: () => `generate-strategy/`,
  // Bookmark endpoints
  bookmarkStrategy: () => `bookmark-strategy/`,
  unbookmarkStrategy: () => `bookmark-strategy/`,
  // Start Trading endpoints
  startStrategy: () => `trading-connection/create/`,
  pauseStrategy: () => `trading-connections/pause/`,
  resumeStrategy: () => `trading-connections/reactivate/`,
  stopStrategy: () => `trading-connections/cancel/`,
  reactivateStrategy: () => `trading-connections/reactivate/`,
  prepareUnsubscribe: (strategyId) =>
    `prepare-unlicense/?strategy=${strategyId}`,
  strategyUnsubscribe: () => `license-strategy/`,
  updateStrategyAllocationAmount: (portfolioId) =>
    `update-trading-allocation/${portfolioId}/`,
  NCBStrategyAPI: (strategyId) => `strategy/nocode/${strategyId}/edit/`,
  getNCBTargetAssets: () => `nocode/assets/`,
  getTradingConnectionInfo: () => `trading-connection/create/`,
  updateTradeConnection: (accountId) =>
    `update-trading-allocation/${accountId}/`,
  getIndustry: () => `dynamic-json-schema?type=INDUSTRY`,
  getBacktestStream: (id) => `${BASE_URL}backtest_stream/?backtest_id=${id}`,
  cancelBacktest: (strategyId, backtestId) =>
    `strategy/${strategyId}/backtest/${backtestId}/cancel/`,
  saveAssetGroups: (id) => `strategy/groups/${id}/`,
  getAssetGroups: (id) => `strategy/groups/${id}/`,
  ncbSettings: (id) => `ncb/strategy/settings/${id}/`,
  updatePredefinedScenario: (id) => `backtest-scenarios/all/${id}/`,
  getAssets: () => `strategy/etf/asset-search`,
  editStrategy: (id) => `strategy/etf/${id}/edit/`,
  getEtfStrategyDetails: (id) => `strategy/etf/${id}/edit/`,
  aiSuggestedEtfAllocation: () => `strategies/ai-suggested-asset-allocation/`,
  strategySettings: (id) => `strategy/settings/${id}/`,

  getCommunityStrategiesDetailsV2: (strategyId) =>
    `community-strategies/${strategyId}/`,
  getStrategyIcon: () => `dynamic-json-schema?type=STRATEGY_ICONS`,
  getStrategyCollection: () => `marketplace/card-collection`,
  getStrategyCollectionDetails: (slug) =>
    `marketplace/card-collection/${slug}/details`,
  deployStrategy: () => `${BASE_URL}trading-connection/create/`,
};

export const deployStrategy = (payload) =>
  post(SERVICE_URLS.deployStrategy(), payload);

export const getStrategyCollectionDetails = (slug) =>
  get(SERVICE_URLS.getStrategyCollectionDetails(slug));

export const getStrategyIcon = () => get(SERVICE_URLS.getStrategyIcon());

export const getStrategyCollection = () =>
  get(SERVICE_URLS.getStrategyCollection());

export const getBacktestStream = (id) =>
  fetchService.get(SERVICE_URLS.getBacktestStream(id));

export const cancelBacktest = (strategyId, backtestId) =>
  post(SERVICE_URLS.cancelBacktest(strategyId, backtestId));

export const getAllPredefinedScenarios = () =>
  get(SERVICE_URLS.predefinedScenariosAPI());

export const createCustomScenario = (payload) =>
  post(SERVICE_URLS.predefinedScenariosAPI(), payload);

export const deleteCustomScenario = (id) => {
  const URL = SERVICE_URLS.predefinedScenariosAPI(id);
  return del(`${URL}${id}/`);
};

export const getStrategyCode = (strategyId) =>
  get(SERVICE_URLS.strategyAPI(strategyId));

export const getStrategies = () => get(SERVICE_URLS.getStrategies());

export const getMyStrategies = (params) =>
  get(SERVICE_URLS.getMyStrategies(), params);

export const getStrategyDetails = (strategyId) =>
  get(SERVICE_URLS.getStrategyDetails(strategyId));

export const getPublicStrategyDetails = (strategyId) =>
  get(SERVICE_URLS.getPublicStrategyDetails(strategyId));

// Explore V2 - Strategy Page services
export const getStrategyOverview = (strategyId) =>
  get(SERVICE_URLS.getStrategyOverview(strategyId));

export const getActiveHoldings = (strategyId) =>
  get(SERVICE_URLS.getActiveHoldings(strategyId));

export const getAuthorOrderHistory = (strategyId, params) =>
  get(SERVICE_URLS.getAuthorOrderHistory(strategyId), params);

export const getSimilarStrategies = (strategyId) =>
  get(SERVICE_URLS.getSimilarStrategies(strategyId));

export const getAllStrategyHoldings = (strategyId) =>
  get(SERVICE_URLS.getAllStrategyHoldings(strategyId));

export const getCommunityStrategies = (params) =>
  get(SERVICE_URLS.getCommunityStrategies(), params);

export const getTrendingStrategies = (params) =>
  get(SERVICE_URLS.getTrendingStrategies(), params);

export const getRecommendedStrategies = () =>
  get(SERVICE_URLS.getRecommendedStrategies());

export const createStrategy = (data) => {
  const payload = {
    coding_level: data.strategyType,
    used_ai: data.usedAi,
  };

  return post(SERVICE_URLS.createStrategy(), payload);
};

export const backtestStrategy = (strategyId, qs, payload) => {
  if (qs) qs = `?${qs}`;
  return post(SERVICE_URLS.backtestStrategy(strategyId, qs), payload);
};

export const saveStrategy = (data) => {
  const formData = new FormData();

  if (data?.name) {
    formData.append("name", data.name);
  }
  if (data?.description) {
    formData.append("description", data.description);
  }

  // If an actual File/Blob is provided, append it under 'strategy_image'
  if (
    data?.strategy_image instanceof File ||
    data?.strategy_image instanceof Blob
  ) {
    const file = data.strategy_image;
    formData.append("strategy_image", file, file.name || "strategy_image");
  }

  // Prefer sending multipart/form-data as per new API contract
  return post(
    SERVICE_URLS.strategyAPI(data?.strategyId || data?.id),
    formData,
    {
      headers: { "Content-Type": "multipart/form-data" },
    }
  );
};

export const deleteStrategy = (strategyId) =>
  del(SERVICE_URLS.strategyAPI(strategyId));

export const deleteStrategies = (strategyIds) => {
  const payload = {
    strategy_ids: strategyIds,
  };
  return del(SERVICE_URLS.strategyBulkDelete(), payload);
};

export const bookmarkStrategy = (id) => {
  const payload = {
    strategy_id: id,
  };

  return post(SERVICE_URLS.bookmarkStrategy(), payload);
};

export const unbookmarkStrategy = (id) => {
  const payload = {
    strategy_id: id,
  };

  return del(SERVICE_URLS.unbookmarkStrategy(), payload);
};

export const startStrategy = (data) => {
  const payload = {
    account_id: data.portfolioId,
    strategy: data.strategyId,
    allocation_pct: data.allocationPercentage,
    dollar_allocation: data.allocationPrice,
  };
  return post(SERVICE_URLS.startStrategy(), payload);
};

export const pauseStrategy = (subscriptionId) => {
  const payload = {
    id: subscriptionId,
  };
  return post(SERVICE_URLS.pauseStrategy(), payload);
};

export const stopStrategy = (subscriptionId) => {
  const payload = {
    id: subscriptionId,
  };

  return post(SERVICE_URLS.stopStrategy(), payload);
};

export const prepareUnsubscribe = (strategyId) =>
  get(SERVICE_URLS.prepareUnsubscribe(strategyId));

export const strategyUnsubscribe = (data) => {
  const payload = {
    strategy_id: data.strategyId,
    flatten: data.isFlat,
  };
  return del(SERVICE_URLS.strategyUnsubscribe(), payload);
};

export const reactivateStrategy = (subscriptionId) => {
  const payload = {
    id: subscriptionId,
  };
  return post(SERVICE_URLS.reactivateStrategy(), payload);
};

export const cloneStrategy = (data) => {
  const payload = {
    strategy_id: data.strategy_id,
    name: data?.name,
  };
  return post(SERVICE_URLS.cloneStrategy(), payload);
};

export const saveStrategyDescription = (payload) =>
  post(SERVICE_URLS.saveStrategyDescription(), payload);

export const hostStrategy = (strategyId, payload) =>
  post(SERVICE_URLS.hostStrategy(strategyId), payload);

export const getStrategyTemplates = (params) =>
  get(SERVICE_URLS.getStrategyTemplates(), params);

export const generateStrategy = (payload) =>
  post(SERVICE_URLS.generateStrategy(), payload);

export const updateStrategyAllocationAmount = (portfolioId, payload) =>
  put(SERVICE_URLS.updateStrategyAllocationAmount(portfolioId), payload);

export const getNCBTargetAssets = (params) =>
  get(SERVICE_URLS.getNCBTargetAssets(), params);

export const saveNCBStrategyBot = (data) => {
  const payload = {
    groups: data?.groups,
    interval: data?.interval,
    assets: data?.assets,
    strategy: data?.strategy,
  };

  return post(SERVICE_URLS.NCBStrategyAPI(data.id), payload);
};

export const getNCBStrategyBot = (strategyId) =>
  get(SERVICE_URLS.NCBStrategyAPI(strategyId));

export const getTradingConnectionInfo = (accountId) => {
  const params = {
    account_id: accountId,
  };
  return get(SERVICE_URLS.getTradingConnectionInfo(), params);
};

export const updateTradeConnection = (data) => {
  const payload = { new_allocation: data.newAllocation };
  return put(SERVICE_URLS.updateTradeConnection(data.accountId), payload);
};

export const getIndustry = () => get(SERVICE_URLS.getIndustry());

export const createStrategyAndRepo = async (data) => {
  const { strategy_id, name } = await createStrategy(data);

  await createRepoGithub({
    strategyId: strategy_id,
    strategyName: name,
    useDefault: true,
  });

  return { strategy_id };
};

export const saveAssetGroups = (id, payload) =>
  post(SERVICE_URLS.saveAssetGroups(id), payload);

export const getAssetGroups = (id) => get(SERVICE_URLS.getAssetGroups(id));

export const saveNcbSettings = (id, payload) =>
  put(SERVICE_URLS.ncbSettings(id), payload);

export const getNcbSettings = (id, payload) =>
  get(SERVICE_URLS.ncbSettings(id), payload);

export const updatePredefinedScenario = (id, payload) =>
  patch(SERVICE_URLS.updatePredefinedScenario(id), payload);

export const getAssets = (params) => get(SERVICE_URLS.getAssets(), params);

export const editStrategy = (data) => {
  const payload = {
    allocations: data?.allocations,
    frequency: data?.frequency,
    name: data?.name,
    period: data?.period,
    trailing: data?.trailing,
  };
  return post(SERVICE_URLS.editStrategy(data.id), payload);
};

export const getEtfStrategyDetails = (id) =>
  get(SERVICE_URLS.getEtfStrategyDetails(id));

// AI Suggested Allocation for ETF assets
export const getAiSuggestedEtfAllocation = (assets) => {
  const payload = { assets };
  return post(SERVICE_URLS.aiSuggestedEtfAllocation(), payload);
};

// Strategy rule settings (Stop Loss / Take Profit)
export const getStrategySettings = (id) =>
  get(SERVICE_URLS.strategySettings(id));

export const updateStrategySettings = (id, payload, params = {}) =>
  put(SERVICE_URLS.strategySettings(id), payload, {
    ...(params || {}),
  });

export const getCommunityStrategiesDetailsV2 = (strategyId) =>
  get(SERVICE_URLS.getCommunityStrategiesDetailsV2(strategyId));

export const resumeStrategy = (subscriptionId) => {
  const payload = {
    id: subscriptionId,
  };
  return post(SERVICE_URLS.resumeStrategy(), payload);
};
